import React, { useState, useMemo } from "react";
import { DollarSign, ChevronDown } from "lucide-react";
import { isDealInPipeline, defaultSalesStages } from "../deals/usePipelines";
import type { Deal, DealPipeline, DealStage, Sale } from "../types";
import type { FilterOptions } from "./dashboardUtils";

interface PipelineHealthCardProps {
  deals: Deal[];
  pipelines: DealPipeline[];
  salesTeam?: Sale[];
  filters?: FilterOptions;
  wonStages: string[];
  lostStages?: string[];
  currency?: string;
  defaultPipelineId?: string;
}

export const PipelineHealthCard: React.FC<PipelineHealthCardProps> = ({
  deals,
  pipelines,
  salesTeam = [],
  filters,
  wonStages,
  defaultPipelineId = "all",
}) => {
  // Funil selecionado no card (ou o selecionado globalmente no dashboard)
  const [selectedPipeId, setSelectedPipeId] = useState<string>(() => {
    if (defaultPipelineId !== "all") return defaultPipelineId;
    return pipelines[0] ? String(pipelines[0].id) : "funil-de-vendas";
  });

  React.useEffect(() => {
    if (defaultPipelineId !== "all") {
      setSelectedPipeId(defaultPipelineId);
    }
  }, [defaultPipelineId]);

  // Funil ativo
  const activePipeline = useMemo(() => {
    return (
      pipelines.find(
        (p) => String(p.id).toLowerCase() === selectedPipeId.toLowerCase(),
      ) ||
      pipelines[0] || {
        id: "funil-de-vendas",
        nome: "Funil SDR - Inbound",
        etapas: defaultSalesStages,
      }
    );
  }, [pipelines, selectedPipeId]);

  // Etapas ordenadas do funil
  const stages: DealStage[] = useMemo(() => {
    if (activePipeline.etapas && activePipeline.etapas.length > 0) {
      return activePipeline.etapas;
    }
    return defaultSalesStages;
  }, [activePipeline]);

  // Filtra apenas negócios pertencentes EXCLUSIVAMENTE a este funil (sem duplicidade)
  const pipeDeals = useMemo(() => {
    const seenDealIds = new Set<string | number>();
    const uniqueDeals: Deal[] = [];

    deals.forEach((d) => {
      if (seenDealIds.has(d.id)) return;

      if (
        isDealInPipeline(
          d,
          activePipeline,
          pipelines,
          String(activePipeline.id),
        )
      ) {
        seenDealIds.add(d.id);
        uniqueDeals.push(d);
      }
    });

    return uniqueDeals;
  }, [deals, activePipeline, pipelines]);

  // Estatísticas de passagem e ganho por etapa
  const { stageStats, winRateOverall } = useMemo(() => {
    const stageValues = stages.map((s) => s.value.toLowerCase().trim());

    // Deduplicação estrita: Sets de IDs únicos de leads
    const passedDealIdsByStage: Record<string, Set<string | number>> = {};
    const wonDealIdsByStage: Record<string, Set<string | number>> = {};

    stageValues.forEach((val) => {
      passedDealIdsByStage[val] = new Set();
      wonDealIdsByStage[val] = new Set();
    });

    let totalWonDealsCount = 0;

    pipeDeals.forEach((deal) => {
      const currentStage = (deal.stage || "").toLowerCase().trim();
      const isWon =
        deal.status === "ganho" ||
        wonStages.some((s) => s.toLowerCase() === currentStage);

      if (isWon) {
        totalWonDealsCount += 1;
      }

      // Determina as etapas pelas quais o lead passou
      const visited = new Set<string>();

      const stageIdx = stageValues.indexOf(currentStage);
      if (stageIdx >= 0) {
        // Se está na etapa N, passou por todas as etapas anteriores 0..N
        for (let i = 0; i <= stageIdx; i++) {
          visited.add(stageValues[i]);
        }
      } else if (isWon) {
        // Se foi ganho, passou por todo o funil até a vitória
        stageValues.forEach((val) => visited.add(val));
      } else {
        // Padrão: primeira etapa
        if (stageValues[0]) visited.add(stageValues[0]);
      }

      // Incorpora histórico se existir
      if (Array.isArray(deal.history)) {
        deal.history.forEach((evt) => {
          if (evt.type === "stage_change") {
            const oldVal = String(evt.old_value || "")
              .toLowerCase()
              .trim();
            const newVal = String(evt.new_value || "")
              .toLowerCase()
              .trim();
            if (stageValues.includes(oldVal)) visited.add(oldVal);
            if (stageValues.includes(newVal)) visited.add(newVal);
          }
        });
      }

      // 1. Marca passagem única em cada etapa que o lead visitou
      visited.forEach((val) => {
        if (passedDealIdsByStage[val]) {
          passedDealIdsByStage[val].add(deal.id);
        }
      });

      // 2. O ganho é contabilizado APENAS na etapa onde o negócio foi ganho
      if (isWon) {
        let wonStageKey = currentStage;
        if (!stageValues.includes(wonStageKey)) {
          // Se currentStage for "won" ou genérico, atribui à etapa de ganho correspondente ou última etapa
          const matchingWon = stageValues.find((val) =>
            wonStages.some((w) => w.toLowerCase() === val),
          );
          wonStageKey = matchingWon || stageValues[stageValues.length - 1];
        }

        if (wonStageKey && wonDealIdsByStage[wonStageKey]) {
          wonDealIdsByStage[wonStageKey].add(deal.id);
        }
      }
    });

    const items = stages.map((stage) => {
      const key = stage.value.toLowerCase().trim();
      const passedCount = passedDealIdsByStage[key]?.size || 0;
      const wonCount = wonDealIdsByStage[key]?.size || 0;

      return {
        stage,
        passedCount,
        wonCount,
      };
    });

    const topCount = items[0]?.passedCount || pipeDeals.length;
    const winRate =
      topCount > 0 ? Math.round((totalWonDealsCount / topCount) * 100) : 0;

    return {
      stageStats: items,
      winRateOverall: winRate,
    };
  }, [stages, pipeDeals, wonStages]);

  // Texto do filtro de vendedor
  const salesFilterLabel = useMemo(() => {
    if (!filters || filters.salesId === "all") return "TODOS";
    const member = salesTeam.find(
      (s) => String(s.id) === String(filters.salesId),
    );
    if (member) {
      return `${member.first_name || ""} ${member.last_name || ""}`
        .trim()
        .toUpperCase();
    }
    return "FILTRADO";
  }, [filters, salesTeam]);

  // Texto do filtro de período
  const periodFilterLabel = useMemo(() => {
    switch (filters?.period) {
      case "this_month":
        return "ESTE MÊS";
      case "last_30_days":
        return "ÚLTIMOS 30 DIAS";
      case "this_quarter":
        return "ESTE TRIMESTRE";
      case "this_year":
        return "ESTE ANO";
      default:
        return "TODO O PERÍODO";
    }
  }, [filters?.period]);

  // Escala Y dinâmica
  const maxVal = Math.max(
    ...stageStats.map((s) => Math.max(s.passedCount, s.wonCount)),
    0,
  );
  const yMax = maxVal === 0 ? 4 : Math.ceil(maxVal / 2) * 2;
  const yTicks = [yMax, Math.round(yMax / 2), 0];

  return (
    <div className="bg-card border border-border/80 rounded-xl p-6 shadow-xs space-y-6">
      {/* 1. Título do Funil com Ícone de Cifrão e Breadcrumbs */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full border border-foreground/60 flex items-center justify-center text-foreground font-bold text-xs">
              <DollarSign className="w-4 h-4 stroke-[2.5]" />
            </div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-foreground tracking-wide uppercase">
                {activePipeline.nome}
              </h2>

              {/* Seletor de Funil */}
              {pipelines.length > 1 && (
                <div className="relative inline-block">
                  <select
                    value={selectedPipeId}
                    onChange={(e) => setSelectedPipeId(e.target.value)}
                    className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                  >
                    {pipelines.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title="Alternar funil"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Linha de Breadcrumb / Badges de Filtros */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground uppercase">
          <span className="font-medium">[VENDAS] {activePipeline.nome}</span>
          <span>·</span>
          <span className="font-medium">ABERTO, PERDIDO, GANHO</span>
          <span>·</span>
          <span className="px-2 py-0.5 rounded-md border border-blue-500/40 text-blue-600 dark:text-blue-400 font-semibold bg-blue-500/5 text-[11px]">
            {periodFilterLabel}
          </span>
          <span>·</span>
          <span className="px-2 py-0.5 rounded-md border border-blue-500/40 text-blue-600 dark:text-blue-400 font-semibold bg-blue-500/5 text-[11px]">
            {salesFilterLabel}
          </span>
        </div>

        {/* Indicador de Taxa de Ganho */}
        <div className="pt-1">
          <span className="text-sm font-semibold text-foreground">
            Taxa de ganho é {winRateOverall}%
          </span>
        </div>
      </div>

      {/* 2. Área do Gráfico de Conversão e Passagem por Etapa */}
      <div className="relative pt-4 pb-2">
        <div className="flex">
          {/* Label vertical do eixo Y */}
          <div className="w-8 shrink-0 flex items-center justify-center">
            <span
              className="text-xs text-muted-foreground whitespace-nowrap transform -rotate-90 select-none"
              style={{ width: "max-content" }}
            >
              Número de negócios
            </span>
          </div>

          {/* Área principal das colunas e linhas do gráfico */}
          <div className="flex-1 relative min-h-[220px] flex flex-col justify-between">
            {/* Linhas horizontais do grid */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-8 pl-4">
              {yTicks.map((tick) => (
                <div key={tick} className="flex items-center w-full gap-2">
                  <span className="text-[11px] text-muted-foreground w-4 text-right">
                    {tick}
                  </span>
                  <div className="flex-1 border-b border-border/40" />
                </div>
              ))}
            </div>

            {/* Colunas por Etapa com Chevrons de Conversão entre elas */}
            <div className="relative z-10 flex items-end justify-between pl-7 pr-2 h-[180px] pb-8">
              {stageStats.map((item, idx) => {
                const isLast = idx === stageStats.length - 1;
                const nextItem = !isLast ? stageStats[idx + 1] : null;

                // Taxa de conversão para a próxima etapa
                const conversionToNext =
                  item.passedCount > 0 && nextItem
                    ? Math.round(
                        (nextItem.passedCount / item.passedCount) * 100,
                      )
                    : 0;

                // Alturas proporcionais das barras
                const passedHeightPercent =
                  yMax > 0 ? (item.passedCount / yMax) * 100 : 0;
                const wonHeightPercent =
                  yMax > 0 ? (item.wonCount / yMax) * 100 : 0;

                return (
                  <div
                    key={item.stage.value}
                    className="flex-1 flex items-end justify-center relative group"
                  >
                    {/* Barras e Números da Etapa */}
                    <div className="flex flex-col items-center">
                      {/* Número acima das colunas */}
                      <span className="text-xs font-semibold text-foreground mb-1 select-none">
                        {item.passedCount}
                      </span>

                      {/* As duas barras: Chegou à etapa e Ganho */}
                      <div className="flex items-end gap-1 h-[120px]">
                        {/* Barra Vermelha/Coral: Chegou à etapa */}
                        <div
                          className="w-4 sm:w-5 rounded-t-xs bg-rose-500 transition-all duration-500"
                          style={{
                            height: `${Math.max(passedHeightPercent, item.passedCount > 0 ? 6 : 0)}%`,
                          }}
                          title={`Chegou à etapa: ${item.passedCount}`}
                        />

                        {/* Barra Laranja: Ganho */}
                        <div
                          className="w-4 sm:w-5 rounded-t-xs bg-amber-500 transition-all duration-500"
                          style={{
                            height: `${Math.max(wonHeightPercent, item.wonCount > 0 ? 6 : 0)}%`,
                          }}
                          title={`Ganho: ${item.wonCount}`}
                        />
                      </div>
                    </div>

                    {/* Chevron com Taxa de Conversão entre as etapas */}
                    {!isLast && (
                      <div className="absolute right-[-14px] sm:right-[-18px] top-[40%] transform -translate-y-1/2 z-20 pointer-events-none">
                        <div
                          className="flex items-center justify-center bg-zinc-600 text-white font-bold text-[10px] sm:text-[11px] px-2 py-0.5 rounded-l-xs shadow-xs"
                          style={{
                            clipPath:
                              "polygon(0% 0%, 78% 0%, 100% 50%, 78% 100%, 0% 100%)",
                            minWidth: "38px",
                            height: "22px",
                          }}
                        >
                          <span className="mr-1">{conversionToNext}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Labels das Etapas no Eixo X */}
            <div className="flex justify-between pl-7 pr-2 pt-2 border-t border-border/60">
              {stageStats.map((item) => (
                <div
                  key={item.stage.value}
                  className="flex-1 text-center px-1"
                  title={item.stage.label}
                >
                  <span className="text-[11px] text-muted-foreground block truncate">
                    {item.stage.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Legenda no Rodapé */}
      <div className="flex items-center gap-6 pt-2 text-xs font-medium">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-rose-500 inline-block shrink-0" />
          <span className="text-foreground">Chegou à etapa</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-amber-500 inline-block shrink-0" />
          <span className="text-foreground">Ganho</span>
        </div>
      </div>
    </div>
  );
};
